import {
  CtaProcessingOption,
  type RestLossAversionCard,
  type RestModalEmailSuccessModal,
  type RestModalForwards,
  type RestMultiRowSurveyCard,
} from '../contracts';
import { RestModalAction, type EventObject } from '../events';
import { type FunnelEventQueue } from './funnel-events';

type ConfirmationContext = Pick<
  EventObject,
  'session' | 'version' | 'app_id' | 'user_id'
> & {
  surveyState: RestMultiRowSurveyCard['provides'];
  isLifecycle?: boolean;
  fallbackCtaId?: string;
};

// Acceptance belongs to the original LA; navigation belongs to its confirmation.
// Keep both in the ordinary queue, without changing transport ordering.
export const buildLaConfirmationSubmission = (
  {
    offerCard,
    offerCta,
    confirmationModal,
  }: {
    offerCard: RestLossAversionCard;
    offerCta: RestModalForwards;
    confirmationModal: RestModalEmailSuccessModal;
  },
  returnAction: RestModalForwards,
  { surveyState, isLifecycle, fallbackCtaId, ...identity }: ConfirmationContext
): FunnelEventQueue => {
  const messageVal = offerCta.sync?.message;
  const commonKeys = {
    ...identity,
    cta_ids: offerCta.cta_details_list?.map((cta) => cta.cta_id) ?? [
      fallbackCtaId,
    ],
    cta_id: offerCta.cta_details_list?.[0]?.cta_id ?? fallbackCtaId,
  };
  const offerKeys = {
    ...commonKeys,
    id: offerCard.id,
    name: offerCard.name,
    card_version: offerCard.version,
  };
  const syncKeys = {
    ...commonKeys,
    direction: 'forwards' as const,
    survey_state: surveyState,
  };
  const offerForm = {
    sync: offerCta.sync,
    message: messageVal,
    survey_state: surveyState,
  };
  return {
    synchronous: true,
    events: [
      {
        ...offerKeys,
        event: RestModalAction.accepted_la_offer,
        properties: {
          sync: offerCta.sync,
          message: messageVal,
          survey_state: surveyState,
        },
      },
    ],
    syncs: [
      {
        ...syncKeys,
        ...offerKeys,
        validation: offerCta.validation,
        type: offerCta.type,
        action: RestModalAction.accept_offer,
        form: offerForm,
      },
      {
        ...syncKeys,
        id: confirmationModal.id,
        name: confirmationModal.name,
        card_version: confirmationModal.version,
        validation: returnAction.validation,
        action: RestModalAction.link,
        form: { ...returnAction.sync, message: messageVal },
        url: isLifecycle ? '?close_iframe' : returnAction.url,
        lco_redirect_url: returnAction.url,
        isCheckoutProcessing:
          returnAction.cta_details_list?.[0]?.processing_option ===
          CtaProcessingOption.chargebee_checkout,
      },
    ],
  };
};
